import type {
  AddComponentHandler,
  ElementData,
  RemoveComponentHandler,
} from '@wix/platform-editor-sdk';
import type {
  EditorScriptFlowAPI,
  FlowEditorSDK,
} from '@wix/yoshi-flow-editor';

import { ElementId, HostWidgetId, WidgetName } from '../../../constants';
import {
  collapseRefComponent,
  expandRefComponentByRole,
} from '../../../editor/editor-sdk-wrappers';
import { withRetry } from '../../../editor/services';
import { getMembersAreaUmbrellaPublicAPI } from '../../../editor/services/members-editor-script';
import { toMonitored } from '../../../editor/services/monitor';
import type {
  LayoutPanelInitialData,
  ManageAndNavigatePanelInitialData,
  OpenManageAndNavigatePanelOptions,
  OpenUnifiedManagerPanelProps,
  TFunction,
} from '../../../types';
import { getLayoutPanelHeight } from '../../../utils';
import { createCustomPanelOnClick } from '../../common/create-panel';
import {
  getProfilePageManageAndNavigateInitialData,
  getSettingsPageManageAndNavigateInitialData,
} from './utils';

const getElementsData = (t: TFunction): ElementData[] => {
  return [
    {
      label: t('app.page.settings.elements.panel.title'),
      identifier: { role: ElementId.HeaderContainer },
      index: 0,
    },
    {
      label: t('app.page.settings.elements.panel.member-menu'),
      identifier: { role: ElementId.MenuContainer },
      index: 1,
    },
  ];
};

export const openSettingsPageDisplayPanel = async (
  flowAPI: EditorScriptFlowAPI,
  editorSDK: FlowEditorSDK,
  event: CustomEvent,
) => {
  const {
    translations: { t },
  } = flowAPI;
  const { componentRef } = event.detail;

  const removeHandler: RemoveComponentHandler = async (compRef, identifier) => {
    await collapseRefComponent(editorSDK, compRef);

    return [identifier];
  };

  const addHandler: AddComponentHandler = async (identifier) => {
    await expandRefComponentByRole(
      editorSDK,
      HostWidgetId.SettingsPage,
      identifier.role,
    );

    return [identifier];
  };

  const elementsData = getElementsData(t as TFunction);

  // TODO: Add helpId
  return editorSDK.editor.openElementsPanel('', {
    widgetRef: componentRef,
    categoriesData: [],
    helpId: '',
    elementsData,
    addComponentHandler: addHandler,
    removeComponentHandler: removeHandler,
  });
};

export const openSettingsPagePresetPanel = async (
  editorSDK: FlowEditorSDK,
  event: CustomEvent,
  { translations, environment }: EditorScriptFlowAPI,
) => {
  const viewport = await editorSDK.editor.info.getCurrentViewport();

  return createCustomPanelOnClick<LayoutPanelInitialData>({
    title: translations.t('app.settings.layout.preset.title'),
    editorSDK,
    panelComp: 'SettingsPageLayoutPanel',
    height: getLayoutPanelHeight(environment),
    width: 288,
    helpId: 'b4c95947-377b-4501-8808-8ded3a70679c',
    initialData: { viewportType: viewport.type },
    widgetName: WidgetName.SettingsPage,
  })(event);
};

export const openProfileManageAndNavigatePanel = async ({
  editorSDK,
  event,
  t,
}: OpenManageAndNavigatePanelOptions) => {
  const initialData = await toMonitored(
    'split-app.profile.manage-and-navigate-panel.get-initial-data',
    () =>
      withRetry(() => getProfilePageManageAndNavigateInitialData(editorSDK), {
        methodName: 'getProfilePageManageAndNavigateInitialData',
      }),
  );

  return createCustomPanelOnClick<ManageAndNavigatePanelInitialData>({
    title: t('app.settings.manage.menu.panel.title'),
    editorSDK,
    panelComp: 'ManageAndNavigatePanel',
    height: 521,
    width: 312,
    helpId: 'f99c3e77-0303-46f3-a5eb-23e565ecdcb8',
    initialData,
    widgetName: WidgetName.Widget1,
  })(event);
};

export const openSettingsManageAndNavigatePanel = async ({
  editorSDK,
  event,
  t,
}: OpenManageAndNavigatePanelOptions) => {
  const initialData = await toMonitored(
    'split-app.settings.manage-and-navigate-panel.get-initial-data',
    () =>
      withRetry(() => getSettingsPageManageAndNavigateInitialData(editorSDK), {
        methodName: 'getSettingsPageManageAndNavigateInitialData',
      }),
  );

  return createCustomPanelOnClick<ManageAndNavigatePanelInitialData>({
    title: t('app.settings.manage.menu.panel.title'),
    editorSDK,
    panelComp: 'ManageAndNavigatePanel',
    height: 521,
    width: 312,
    helpId: 'f99c3e77-0303-46f3-a5eb-23e565ecdcb8',
    initialData,
    widgetName: WidgetName.SettingsPage,
  })(event);
};

export const openUnifiedManagerPanel = async (
  editorSDK: FlowEditorSDK,
  params: OpenUnifiedManagerPanelProps,
) => {
  const membersAreaPublicAPI = await getMembersAreaUmbrellaPublicAPI(editorSDK);
  return membersAreaPublicAPI.openUnifiedManagerPanel(params);
};
